// CategoriesMobile.jsx — onglet Catégories (mobile). CRUD complet.
// Mêmes endpoints que GestionCategories.jsx (/categories, /categories/upload).
import React, { useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import axios from "axios";
import API_CONFIG from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import useBusinessMutationInvalidation from "../../hooks/useBusinessMutationInvalidation";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Fab, Sheet, ActionRow,
  Field, TextInput, TextAreaInput, SelectInput, Mono, Pill, CountChip, Ico,
  useResourceList, useMobileMenu, genSlug, fmt,
} from "./productMobileKit";

const API_URL = API_CONFIG.apiUrl("/categories");
const PERM = {
  create: "Gestion Commerciale.Produits.Categories.create",
  edit: "Gestion Commerciale.Produits.Categories.edit",
  delete: "Gestion Commerciale.Produits.Categories.delete",
};
const countOf = (c) => {
  const n = c.products_count ?? c.product_count ?? c.products;
  return Number.isFinite(Number(n)) ? Number(n) : null;
};
const resolveImg = (img) =>
  !img ? null : (String(img).startsWith("http") ? img : `${API_CONFIG.BASE_URL}${String(img).startsWith("/") ? "" : "/"}${img}`);

export default function CategoriesMobile({ onMenu: onMenuProp }) {
  const invalidateCategories = useBusinessMutationInvalidation("product-category");
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const { hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const can = { create: hasPermission(PERM.create), edit: hasPermission(PERM.edit), delete: hasPermission(PERM.delete) };
  const fileRef = useRef(null);

  const { rows: cats, loading, reload } = useResourceList(
    async () => (selectedCompany ? (await axios.get(API_URL, { params: { company_id: selectedCompany } })).data : []),
    [selectedCompany]
  );

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sheet, setSheet] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", description: "", parent_id: null, image: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const hasCounts = useMemo(() => cats.some((c) => countOf(c) != null), [cats]);
  const roots = useMemo(() => cats.filter((c) => !c.parent_id).length, [cats]);
  const totalProducts = useMemo(() => cats.reduce((s, c) => s + (countOf(c) || 0), 0), [cats]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cats.filter((c) => {
      if (filter === "roots" && c.parent_id) return false;
      if (filter === "subs" && !c.parent_id) return false;
      if (q && ![c.name, c.slug, c.description, c.parent_name].some((v) => String(v || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [cats, query, filter]);

  const parentOptions = useMemo(() => {
    const selfId = sheet?.cat?.id;
    return cats.filter((c) => c.id !== selfId).map((c) => ({ value: c.id, label: c.name }));
  }, [cats, sheet]);

  const openCreate = () => {
    if (!selectedCompany) return message.warning("Veuillez sélectionner une entreprise");
    setForm({ name: "", slug: "", description: "", parent_id: null, image: "" });
    setSheet({ mode: "form", cat: null });
  };
  const openEdit = (c) => {
    setForm({ name: c.name || "", slug: c.slug || "", description: c.description || "", parent_id: c.parent_id || null, image: c.image || "" });
    setSheet({ mode: "form", cat: c });
  };
  const openActions = (c) => {
    if (can.edit && !can.delete) return openEdit(c);
    if (!can.edit && !can.delete) return;
    setSheet({ mode: "actions", cat: c });
  };
  const close = () => { if (!saving) setSheet(null); };
  const onName = (v) => setForm((f) => ({ ...f, name: v, slug: genSlug(v) }));

  const onPickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API_URL}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: res.data?.filePath || res.data?.path || "" }));
      message.success("Image téléversée");
    } catch (e) { message.error("Erreur lors du téléversement de l'image"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) return message.warning("Veuillez saisir le nom");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), slug: form.slug || genSlug(form.name),
        description: form.description || "", parent_id: form.parent_id || null,
        image: form.image || "", company_id: selectedCompany,
      };
      if (sheet.cat) { await axios.put(`${API_URL}/${sheet.cat.id}`, payload); message.success("Catégorie mise à jour avec succès"); }
      else { await axios.post(API_URL, payload); message.success("Catégorie créée avec succès"); }
      void invalidateCategories();
      setSheet(null); reload();
    } catch (err) { message.error(err.response?.data?.error || "Erreur lors de la sauvegarde de la catégorie"); }
    finally { setSaving(false); }
  };

  const askDelete = (c) => {
    setSheet(null);
    Modal.confirm({
      title: "Supprimer cette catégorie ?", content: "Cette action pourrait affecter les produits associés.",
      okText: "Oui, Supprimer", okType: "danger", cancelText: "Non", centered: true,
      onOk: async () => {
        try { await axios.delete(`${API_URL}/${c.id}`); void invalidateCategories(); message.success("Catégorie supprimée avec succès"); reload(); }
        catch (err) {
          if (err.response?.status === 409 && err.response?.data?.error) message.error(err.response.data.error);
          else message.error("Erreur lors de la suppression de la catégorie.");
        }
      },
    });
  };

  const cardThumb = (c) => {
    const url = resolveImg(c.image);
    if (url) return <div className="mono mono--sq" style={{ padding: 0, overflow: "hidden", background: "#fff" }}><img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>;
    return <Mono name={c.name} square />;
  };

  return (
    <ProductScreen>
      <Appbar title="Catégories" onMenu={onMenu}
        subtitle={`${cats.length} catégorie${cats.length > 1 ? "s" : ""} · ${roots} racine${roots > 1 ? "s" : ""}`}
        action={<button className="iconbtn"><Ico name="sliders-horizontal" /></button>} />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Summary items={[
          { label: "Catégories", value: cats.length },
          { label: "Racines", value: roots },
          hasCounts && { label: "Produits liés", value: fmt(totalProducts) },
        ]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Rechercher une catégorie…" />
        <Chips value={filter} onChange={setFilter} options={[
          { key: "all", label: "Toutes" }, { key: "roots", label: "Racines" }, { key: "subs", label: "Sous-catégories" },
        ]} />
      </div>

      <div className="body">
        {loading || !visible.length ? (
          <EmptyState icon="layers" loading={loading}
            title={loading ? "Chargement des catégories" : (query || filter !== "all" ? "Aucun résultat" : "Aucune catégorie")}
            text={loading ? "Synchronisation en cours." : "Organisez vos produits en créant une première catégorie."} />
        ) : (
          <div className="list">
            {visible.map((c) => (
              <button key={c.id} className="rcard" onClick={() => openActions(c)}>
                {cardThumb(c)}
                <div className="rcard__main">
                  <div className="rcard__name">{c.name}</div>
                  <div className="rcard__meta">
                    {c.parent_name
                      ? <Pill tone="info" label={c.parent_name} />
                      : <span className="rcard__sub">Catégorie racine</span>}
                    <CountChip value={countOf(c)} />
                  </div>
                </div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={openCreate} label="Nouvelle" /> : null}

      <Sheet open={sheet?.mode === "form"} onClose={close}
        title={sheet?.cat ? "Modifier la catégorie" : "Nouvelle catégorie"}
        subtitle="Regroupez vos produits par familles.">
        <Field label="Nom de la catégorie">
          <TextInput value={form.name} onChange={onName} placeholder="Ex : Électronique" icon="layers" autoFocus />
        </Field>
        <Field label="Slug" hint="(auto-généré)">
          <TextInput value={form.slug} readOnly placeholder="sera-genere-automatiquement" />
        </Field>
        <Field label="Catégorie parente" hint="facultatif">
          <SelectInput value={form.parent_id} onChange={(v) => setForm((f) => ({ ...f, parent_id: v }))}
            options={parentOptions} placeholder="Aucune (racine)" />
        </Field>
        <div className="field">
          <span className="field__lbl">Image <small>facultatif</small></span>
          <div className="imgfield">
            <div className="imgfield__box">
              {form.image ? <img src={resolveImg(form.image)} alt="" /> : <Ico name="camera" />}
            </div>
            <div>
              <button type="button" className="imgfield__btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? "Téléversement…" : (form.image ? "Changer l'image" : "Ajouter une image")}
              </button>
              <div className="imgfield__hint">PNG ou JPG, format carré conseillé.</div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => onPickImage(e.target.files?.[0])} />
          </div>
        </div>
        <Field label="Description" hint="facultatif">
          <TextAreaInput value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder="Brève description…" />
        </Field>
        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={close} disabled={saving}>Annuler</button>
          <button className="btn btn--primary" onClick={submit} disabled={saving || !form.name.trim()}>
            <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
            {sheet?.cat ? "Mettre à jour" : "Créer la catégorie"}
          </button>
        </div>
      </Sheet>

      <Sheet open={sheet?.mode === "actions"} onClose={close}>
        {sheet?.cat ? (
          <>
            <div className="preview">
              {cardThumb(sheet.cat)}
              <div>
                <div className="preview__name">{sheet.cat.name}</div>
                <div className="preview__slug">{sheet.cat.parent_name ? `Dans ${sheet.cat.parent_name}` : "Catégorie racine"}</div>
              </div>
            </div>
            {can.edit ? <ActionRow icon="pencil" label="Modifier la catégorie" onClick={() => openEdit(sheet.cat)} /> : null}
            {can.delete ? <ActionRow icon="trash-2" label="Supprimer la catégorie" danger onClick={() => askDelete(sheet.cat)} /> : null}
          </>
        ) : null}
      </Sheet>
    </ProductScreen>
  );
}
